import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import { useEffect, useState } from "react";
import SearchBox from "../../components/inputs/search-box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faSmile,
} from "@fortawesome/free-solid-svg-icons";
import TextInput from "../../components/inputs/text";
import { CircularProgress } from "@mui/material";
import { PublicTransmedRegistration } from "../../dtos/transmed/process/registration/registration";
import TransmedRegistrationContestationService from "../../service/transmed/process/registration/contestation";

export const path = "transmed/contestations";

export async function loader(): Promise<PublicTransmedRegistration[]> {
  return await new TransmedRegistrationContestationService().read.outgoings();
}

export function Component() {
  const registrations = useLoaderData() as PublicTransmedRegistration[];
  const [search, setSearch] = useState<string>("");
  const [registrationId, setRegistrationId] = useState<number | null>(null);
  const [answer, setAnswer] = useState<string>("");

  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [loading, setLoading] = useState<boolean>(false);
  const revalidator = useRevalidator();

  useEffect(() => {
    if (registrationId !== null) {
      setAnswer(
        registrations.find((r) => r.id === registrationId)?.answer ?? ""
      );
    }
  }, [registrationId, registrations]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "var(--gap-medium)",
      }}
    >
      <h1 style={{ wordBreak: "break-word" }}>Contestații transmed</h1>
      <SearchBox
        placeholder="Caută după nume sau id public..."
        onSearch={setSearch}
        searchOnClear={true}
      />
      {(search.trim().length === 0
        ? registrations
        : registrations.filter((registration) =>
            (
              registration.user.first_name +
              " " +
              registration.user.last_name +
              " " +
              registration.user.public_id
            ).includes(search.trim())
          )
      ).filter((registration) => registration.question !== null).length ===
      0 ? (
        <span>
          <FontAwesomeIcon icon={faSmile} color="var(--color-fasmr-blue)" />{" "}
          Nicio contestație din partea centrului tău
        </span>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "var(--gap-small)",
          }}
        >
          {(search.trim().length === 0
            ? registrations
            : registrations.filter((registration) =>
                (
                  registration.user.first_name +
                  " " +
                  registration.user.last_name +
                  " " +
                  registration.user.public_id
                ).includes(search.trim())
              )
          )
            .filter((registration) => registration.question !== null)
            .map((registration) => (
              <form
                key={registration.id}
                className="surface"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--gap-small)",
                  alignItems: "stretch",
                }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  setError("");
                  setLoading(true);
                  try {
                    await new TransmedRegistrationContestationService().update.answer(
                      registration.id,
                      answer
                    );
                    revalidator.revalidate();
                  } catch (error) {
                    setError(error);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    gap: "var(--gap-small)",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      wordBreak: "break-word",
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--gap-xs)",
                      flexWrap: "wrap",
                    }}
                  >
                    <h3 style={{ wordBreak: "break-word" }}>
                      {registration.user.first_name}{" "}
                      {registration.user.last_name}
                    </h3>
                    <i style={{ wordBreak: "break-word" }}>
                      ({registration.user.public_id})
                    </i>
                  </span>
                  <div className="stretcher" />
                  <div
                    className="label"
                    style={{
                      padding: "var(--gap-xs) var(--gap-small)",
                      borderRadius: 1000,
                      color:
                        registration.answer === null
                          ? "var(--color-dark)"
                          : "white",
                      backgroundColor:
                        registration.answer === null ? "yellow" : "green",
                    }}
                  >
                    {registration.answer === null ? "Fără răspuns" : "Răspuns"}
                  </div>
                  <button
                    type="button"
                    className="text button primary"
                    onClick={() =>
                      setRegistrationId((rid) =>
                        rid === registration.id ? null : registration.id
                      )
                    }
                  >
                    <FontAwesomeIcon
                      icon={
                        registration.id === registrationId
                          ? faChevronUp
                          : faChevronDown
                      }
                    />
                  </button>
                </div>
                {registration.id === registrationId && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: "var(--gap-small)",
                        flexDirection: "row",
                        flexWrap: "wrap",
                      }}
                    >
                      <TextInput
                        label="Contestație"
                        sx={{ flexGrow: 1 }}
                        multiline
                        rows={6}
                        InputProps={{
                          readOnly: true,
                        }}
                        onKeyDown={(e) => e.preventDefault()}
                        value={registration.question}
                      />
                      <TextInput
                        label="Răspuns"
                        required
                        sx={{ flexGrow: 1 }}
                        multiline
                        rows={6}
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                      />
                    </div>
                    <div
                      style={{
                        alignSelf: "flex-end",
                        display: "flex",
                        gap: "var(--gap-small)",
                      }}
                    >
                      {(loading || revalidator.state === "loading") && (
                        <CircularProgress />
                      )}
                      <button
                        type="submit"
                        className="primary button"
                        disabled={answer === registration.answer}
                      >
                        Salvează răspunsul
                      </button>
                    </div>
                  </>
                )}
              </form>
            ))}
        </div>
      )}
    </div>
  );
}
Component.displayName = "page-account-transmed-contestations";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-contestations-error";
